import Decimal from "decimal.js";
import { useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import UiSideModal from "components/base/UiSideModal";
import UiButton from "components/base/UiButton";
import TextField from "./CustomTextField";
import {
  Custom,
  CostRow,
  rowQuantity,
  calculateCustom,
  money,
  changeTruckingUnit,
  truckingUnits,
  TruckingUnit,
  normalizeTrucking,
} from "./calculator";
import {
  resolveHelpers,
  alignProductionUnit,
  simplifyHauling,
  haulingQuantity,
  linkSelectedCrew,
  ensureRowIds,
  ProductionHelper,
  TravelHelper,
  HaulingHelper,
} from "./helpers";
import CustomMaterialQuantity from "./CustomMaterialQuantity";

const truckingSettings = (row: CostRow): HaulingHelper =>
  row.hauling ?? {
    planningEnabled: true,
    quantityMode: "product_tons",
    haulUnit: "Tons",
    materialRowId: "",
    allocationPercent: "100",
    capacity: String(row.truckCapacity ?? ""),
    capacityUnit: "Tons",
    roundTripHours: "1",
    loadHours: "0",
    unloadHours: "0",
    fullLoads: false,
  };

export default function CustomCalculationsDrawer({
  item,
  onApply,
  onClose,
  readOnly,
  settings,
  initialTab = 0,
  rowIndex,
}: {
  item: Custom;
  onApply: (item: Custom) => void;
  onClose: () => void;
  readOnly: boolean;
  settings: any;
  initialTab?: number;
  rowIndex?: number;
}) {
  const [draft, setDraft] = useState(() => {
      const next = linkSelectedCrew(
        ensureRowIds({
          ...item,
          helpers: {
            ...item.helpers,
            hoursPerDay:
              item.helpers?.production?.enabled || item.helpers?.travel?.enabled
                ? item.helpers.hoursPerDay
                : String(settings?.customSettings?.workingHoursPerDay ?? "8"),
            production: item.helpers?.production
              ? alignProductionUnit(item, item.helpers.production)
              : undefined,
          },
        }),
        rowIndex
      );
      return {
        ...next,
        rows: next.rows.map((row, index) =>
          index === rowIndex && row.category === "trucking"
            ? {
                ...row,
                quantitySource: "trucking" as const,
                hauling: simplifyHauling(
                  next,
                  row,
                  (material) => rowQuantity(next, material).quantity
                ),
                quantityOverride: row.quantitySource === "trucking" ? row.quantityOverride : null,
              }
            : row
        ),
      };
    }),
    [tab, setTab] = useState(initialTab),
    [error, setError] = useState("");
  const [rateEntered, setRateEntered] = useState(!!draft.helpers?.production?.enabled);
  const selectedRow = rowIndex === undefined ? undefined : draft.rows[rowIndex];
  const crewDrawer = selectedRow?.category === "labor";
  const hourlyRow = selectedRow && ["labor", "equipment"].includes(selectedRow.category);
  const showRow = (index: number) => rowIndex === undefined || rowIndex === index;
  const h = draft.helpers ?? {
    hoursPerDay: String(settings?.customSettings?.workingHoursPerDay ?? "8"),
  };
  const p: ProductionHelper = h.production ?? {
    enabled: false,
    source: "item",
    unit: draft.unit,
    rate: "",
    period: "hour",
  };
  const t: TravelHelper = h.travel ?? { enabled: false, roundTripHours: "0", visits: null };
  const setRow = (i: number, patch: Partial<CostRow>) =>
    setDraft((old) => ({
      ...old,
      rows: old.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)),
    }));
  const production = (patch: Partial<ProductionHelper>) => {
    const next = alignProductionUnit(draft, { ...p, ...patch });
    const editingRate = patch.rate !== undefined;
    const hasRate = next.rate !== "";
    if (editingRate || !hasRate) setRateEntered(hasRate);
    setDraft((old) =>
      linkSelectedCrew(
        {
          ...old,
          helpers: {
            ...h,
            production: {
              ...next,
              enabled: hasRate && (editingRate || rateEntered),
            },
          },
        },
        rowIndex
      )
    );
  };
  const travel = (patch: Partial<TravelHelper>) =>
    setDraft((old) =>
      linkSelectedCrew({ ...old, helpers: { ...h, travel: { ...t, ...patch } } }, rowIndex)
    );
  const materials = draft.rows.filter((r) => r.category === "material");
  const field = (
    label: string,
    value: string | undefined | null,
    onChange: (v: string) => void,
    optional = false
  ) => (
    <TextField
      fullWidth
      variant="outlined"
      InputLabelProps={{ shrink: true }}
      size="small"
      label={label}
      value={value ?? ""}
      preserveBlank={optional}
      disabled={readOnly}
      inputProps={{ inputMode: "decimal" }}
      onChange={(e) => onChange(e.target.value)}
    />
  );
  const select = (
    label: string,
    value: string,
    options: { value: string; label: string }[],
    onChange: (v: string) => void
  ) => (
    <TextField
      fullWidth
      variant="outlined"
      InputLabelProps={{ shrink: true }}
      size="small"
      select
      label={label}
      value={value}
      disabled={readOnly}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
  const toggle = (label: string, checked: boolean, onChange: (v: boolean) => void) => (
    <FormControlLabel
      sx={{ m: 0, minWidth: 0, "& .MuiFormControlLabel-label": { overflowWrap: "anywhere" } }}
      label={label}
      control={<Checkbox checked={checked} disabled={readOnly} onChange={(_, v) => onChange(v)} />}
    />
  );
  const disableTravel = () => {
    try {
      const resolved = resolveHelpers(draft, (r) => rowQuantity(draft, r).quantity);
      setDraft({
        ...draft,
        helpers: { ...h, travel: { ...t, enabled: false } },
        rows: draft.rows.map((r, i) => {
          const source = r.quantitySource ?? "manual";
          if (!source.includes("travel")) return r;
          return {
            ...r,
            quantity: resolved.rows[i].quantity,
            trucksNeeded: resolved.rows[i].trucksNeeded,
            quantitySource: "manual",
            quantityOverride: null,
          };
        }),
      });
      setError("");
    } catch (e) {
      setError((e as Error).message);
    }
  };
  let preview: ReturnType<typeof resolveHelpers> | null = null,
    previewError = "",
    total = "";
  try {
    preview = resolveHelpers(draft, (r) => rowQuantity(draft, r).quantity);
    total = money(calculateCustom(draft).cost);
  } catch (e) {
    previewError = (e as Error).message;
  }
  let workingHours: string | null = null;
  try {
    const withoutTrucking = {
      ...draft,
      rows: draft.rows.filter((row) => row.category !== "trucking"),
    };
    workingHours =
      resolveHelpers(
        withoutTrucking,
        (row) => rowQuantity(withoutTrucking, row).quantity
      ).hours?.toString() ?? null;
  } catch {}
  const linked = (r: CostRow, kind: "duration" | "travel", checked: boolean) => {
    const parts = (r.quantitySource ?? "")
      .split("_")
      .filter((v) => v === "duration" || v === "travel");
    const next = checked ? [...new Set([...parts, kind])] : parts.filter((v) => v !== kind);
    return (
      next.includes("duration") && next.includes("travel") ? "duration_travel" : next[0] ?? "manual"
    ) as CostRow["quantitySource"];
  };
  return (
    <UiSideModal
      open
      title={
        selectedRow?.category === "trucking"
          ? "Calculate trucking needs"
          : selectedRow?.category === "equipment"
          ? "Calculate equipment hours"
          : "Calculate labor hours"
      }
      width="680px"
      onClose={onClose}
    >
      <Box
        sx={{
          p: 2,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 2,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          "& > *": { minWidth: 0, maxWidth: "100%" },
          "& .MuiFormControl-root": { minWidth: 0, maxWidth: "100%" },
          "& .MuiInputBase-root": { minWidth: 0, width: "100%" },
          "& .MuiOutlinedInput-root": { height: 40, minHeight: 40 },
          "& .MuiInputBase-input": { minWidth: 0, padding: "8px 12px", lineHeight: "22px" },
          "& .MuiSelect-select.MuiSelect-select": {
            padding: "8px 32px 8px 12px",
            minHeight: "22px",
            lineHeight: "22px",
          },
          "& .MuiTypography-root, & .MuiAlert-message": { overflowWrap: "anywhere", minWidth: 0 },
        }}
      >
        <Box sx={{ display: "grid", gap: 0.75 }}>
          <Typography sx={{ fontSize: 13 }}>
            {selectedRow?.category === "trucking"
              ? "Enter the quantity to haul, truck capacity, cycle time, and available time to estimate trucking needs."
              : selectedRow?.category === "equipment"
              ? "Use the working time and optional travel time to calculate this equipment’s hours."
              : "Enter your production rate to calculate the time needed for this work. Add travel time if needed."}
          </Typography>
          {hourlyRow && (
            <Typography
              variant="body2"
              component="p"
              noWrap={false}
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "normal",
                fontSize: 13,
                lineHeight: 1.5,
                m: 0,
              }}
            >
              {crewDrawer
                ? "These settings apply to this hourly crew and other linked costs. New hourly labor rows inherit the configured labor time; manual quantity overrides stay independent."
                : "Working duration and travel settings are shared by this product. Changes also affect other rows already linked to them."}
            </Typography>
          )}
          {hourlyRow && (
            <Typography sx={{ fontSize: 13 }}>
              Enter a production rate to calculate job time. Apply saves the calculation.
            </Typography>
          )}
        </Box>
        {selectedRow && (
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {crewDrawer
              ? "Calculated Job Time is shared across all Labor added to the Product."
              : selectedRow.name}
          </Typography>
        )}
        {(!selectedRow || hourlyRow) && (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant={hourlyRow ? "fullWidth" : "scrollable"}
            sx={{
              minWidth: 0,
              "& .MuiTab-root": {
                minWidth: 0,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                px: 1,
              },
            }}
          >
            {!selectedRow && (
              <Tab
                value={0}
                label={`Material Quantity${
                  materials.some((r) => r.calculation !== "manual") ? " •" : ""
                }`}
              />
            )}
            <Tab value={1} label={`Production and Job Time${p.enabled ? " •" : ""}`} />
            <Tab value={2} label={`Travel${t.enabled ? " •" : ""}`} />
            {!selectedRow && (
              <Tab
                value={3}
                label={`Trucking${
                  draft.rows.some((r) => r.quantitySource === "trucking") ? " •" : ""
                }`}
              />
            )}
          </Tabs>
        )}
        {hourlyRow && selectedRow.unit !== "Hours" && (
          <Alert severity="info">
            Only hourly costs can follow duration or travel. Change this row’s unit to Hours to link
            it.
          </Alert>
        )}
        {tab === 0 && (
          <>
            {!materials.length && (
              <Alert severity="info">
                Add a material cost first, then configure its quantity here or from its calculator
                button.
              </Alert>
            )}
            {draft.rows.map(
              (r, i) =>
                showRow(i) &&
                r.category === "material" && (
                  <Box key={r.rowId} sx={{ borderBottom: "1px solid #ddd", pb: 2 }}>
                    <Typography>{r.name}</Typography>
                    <CustomMaterialQuantity
                      item={draft}
                      row={r}
                      onChange={(patch) => setRow(i, patch)}
                      readOnly={readOnly}
                      busy={false}
                      settings={settings}
                    />
                  </Box>
                )
            )}
          </>
        )}
        {tab === 1 && (
          <>
            {select(
              "Production quantity source",
              p.source,
              [
                { value: "item", label: "Job Size Measurement" },
                { value: "material", label: "Material quantity" },
              ],
              (v) => production({ source: v as ProductionHelper["source"] })
            )}
            {p.source === "material" &&
              select(
                "Material",
                p.materialRowId ?? "",
                materials.map((r) => ({ value: r.rowId!, label: r.name })),
                (v) => production({ materialRowId: v })
              )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(4, minmax(0, 1fr))",
                },
                gap: 1.5,
                "& .MuiInputLabel-root": { fontSize: 11 },
                "& .MuiInputBase-root": { fontSize: 13 },
              }}
            >
              {field(
                "Production Rate",
                rateEntered ? p.rate : "",
                (v) => production({ rate: v }),
                true
              )}
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                label="Production Unit"
                value={p.unit}
                InputProps={{ readOnly: true }}
              />
              {select(
                "Rate Period",
                p.period,
                [
                  { value: "hour", label: "Per hour" },
                  { value: "day", label: "Per workday" },
                ],
                (v) => production({ period: v as "hour" | "day" })
              )}
              {field("Working Hours per Day", h.hoursPerDay, (v) =>
                setDraft({ ...draft, helpers: { ...h, hoursPerDay: v } })
              )}
            </Box>
            {p.enabled && preview?.hours && !previewError && (
              <Alert severity="info">
                Calculated Job Time: {preview.hours.toDecimalPlaces(4).toString()} hours
              </Alert>
            )}
            {!crewDrawer && (
              <Typography variant="body2">
                Working duration is elapsed time, not total employee-hours. Select hourly costs that
                follow it.
              </Typography>
            )}
            {draft.rows.map(
              (r, i) =>
                !crewDrawer &&
                showRow(i) &&
                ["labor", "equipment"].includes(r.category) &&
                r.unit === "Hours" && (
                  <Box key={r.rowId}>
                    {toggle(r.name, r.quantitySource?.includes("duration") ?? false, (v) =>
                      setRow(i, {
                        quantitySource: linked(r, "duration", v),
                        quantity: preview?.rows[i].quantity ?? r.quantity,
                        quantityOverride: null,
                      })
                    )}
                  </Box>
                )
            )}
          </>
        )}
        {tab === 2 && (
          <>
            {toggle("Include travel", t.enabled, (v) =>
              v ? travel({ enabled: true }) : disableTravel()
            )}
            {t.enabled && (
              <>
                {field("Round-trip travel hours", t.roundTripHours, (v) =>
                  travel({ roundTripHours: v })
                )}
                {field(
                  "Visits (blank = one per workday)",
                  t.visits,
                  (v) => travel({ visits: v || null }),
                  true
                )}
                <Typography variant="body2">
                  Travel adds billed hours only to selected hourly costs. Material needs and
                  production quantity stay unchanged.
                </Typography>
                {draft.rows.map(
                  (r, i) =>
                    !crewDrawer &&
                    showRow(i) &&
                    ["labor", "equipment"].includes(r.category) &&
                    r.unit === "Hours" && (
                      <Box key={r.rowId}>
                        {toggle(r.name, r.quantitySource?.includes("travel") ?? false, (v) =>
                          setRow(i, {
                            quantitySource: linked(r, "travel", v),
                            quantity: preview?.rows[i].quantity ?? r.quantity,
                            quantityOverride: null,
                          })
                        )}
                      </Box>
                    )
                )}
              </>
            )}
          </>
        )}
        {tab === 3 && (
          <>
            {!draft.rows.some((r) => r.category === "trucking") && (
              <Alert severity="info">
                Add a trucking cost first. Each planned row needs its own material allocation.
              </Alert>
            )}
            {draft.rows.map((r, i) => {
              if (!showRow(i) || r.category !== "trucking") return null;
              let c = truckingSettings(r);
              try {
                c = simplifyHauling(draft, r, (material) => rowQuantity(draft, material).quantity);
              } catch {}
              let haulQuantity = "";
              try {
                haulQuantity = haulingQuantity(
                  draft,
                  c,
                  (row) => rowQuantity(draft, row).quantity
                ).toString();
              } catch {}
              const truck = normalizeTrucking(r);
              const hourly = truck.truckingUnit === "Hours";
              const planning = true;
              const update = (patch: Partial<HaulingHelper>) =>
                setRow(i, {
                  hauling: {
                    ...c,
                    quantityMode: c.quantityMode ?? "product_tons",
                    haulUnit: c.haulUnit ?? "Tons",
                    planningEnabled: true,
                    loadHours: "0",
                    unloadHours: "0",
                    maxTripsPerDay: null,
                    ...patch,
                  },
                  quantitySource: "trucking",
                  quantityOverride: r.quantitySource === "trucking" ? r.quantityOverride : null,
                });
              return (
                <Box
                  key={r.rowId}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr)",
                    minWidth: 0,
                    gap: 1,
                    borderBottom: "1px solid #ddd",
                    pb: 2,
                  }}
                >
                  {!selectedRow && <Typography variant="h6">{r.name || "Trucking"}</Typography>}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                      gap: 2,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      label="Total quantity to haul"
                      inputProps={{ inputMode: "decimal" }}
                      value={haulQuantity}
                      preserveBlank
                      overridden={c.quantityMode === "manual"}
                      disabled={readOnly}
                      onChange={(e) =>
                        update({ quantityMode: "manual", haulQuantity: e.target.value })
                      }
                    />
                    {select(
                      "Unit",
                      c.haulUnit ?? "Tons",
                      [
                        { value: "Tons", label: "US tons" },
                        { value: "Yards", label: "Cubic yards" },
                      ],
                      (unit) =>
                        update({
                          haulUnit: unit as "Tons" | "Yards",
                          capacityUnit: unit as "Tons" | "Yards",
                          quantityMode: "manual",
                          haulQuantity: "",
                          capacity: unit === "Tons" ? String(r.truckCapacity ?? "") : "",
                        })
                    )}
                  </Box>
                  {c.quantityMode === "manual" ? (
                    <UiButton
                      uiVariant="outlined-small"
                      disabled={readOnly}
                      onClick={() =>
                        update({
                          quantityMode: "product_tons",
                          haulQuantity: null,
                          haulUnit: "Tons",
                          capacityUnit: "Tons",
                          capacity:
                            c.capacityUnit === "Tons" ? c.capacity : String(r.truckCapacity ?? ""),
                        })
                      }
                    >
                      Restore material quantity
                    </UiButton>
                  ) : (
                    <Typography variant="caption">
                      Linked to this product’s ton-based materials.
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    label={`Capacity per truck (${c.capacityUnit})`}
                    inputProps={{ inputMode: "decimal" }}
                    value={c.capacity}
                    preserveBlank
                    overridden={
                      c.capacityUnit === "Tons" &&
                      r.truckCapacity != null &&
                      c.capacity !== String(r.truckCapacity)
                    }
                    disabled={readOnly}
                    onChange={(e) => update({ capacity: e.target.value })}
                  />
                  {c.capacityUnit === "Tons" &&
                    r.truckCapacity != null &&
                    c.capacity !== String(r.truckCapacity) && (
                      <UiButton
                        uiVariant="outlined-small"
                        disabled={readOnly}
                        onClick={() => update({ capacity: String(r.truckCapacity) })}
                      >
                        Restore truck capacity
                      </UiButton>
                    )}
                  {field("Round-trip cycle time (hours)", c.roundTripHours, (roundTripHours) =>
                    update({ roundTripHours })
                  )}
                  <Typography variant="caption">
                    Include travel, loading, and unloading in the cycle time.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    label="Available job time (hours)"
                    inputProps={{ inputMode: "decimal" }}
                    value={c.planningHours ?? workingHours ?? h.hoursPerDay ?? "8"}
                    overridden={c.planningHours != null}
                    disabled={readOnly}
                    onChange={(e) => update({ planningHours: e.target.value })}
                  />
                  {c.planningHours != null ? (
                    <UiButton
                      uiVariant="outlined-small"
                      disabled={readOnly}
                      onClick={() => update({ planningHours: null })}
                    >
                      Restore job time
                    </UiButton>
                  ) : (
                    <Typography variant="caption">
                      {workingHours
                        ? "Linked to production job time, excluding travel."
                        : "Uses working hours per day."}
                    </Typography>
                  )}
                  {["Tons", "Yards"].includes(truck.truckingUnit) &&
                    toggle("Charge partial loads as full loads", c.fullLoads, (fullLoads) =>
                      update({ fullLoads })
                    )}
                  {preview && r.quantitySource === "trucking" && (
                    <Alert severity="info">
                      {preview.plans[r.rowId!]} loads{" "}
                      {planning ? `· ${preview.rows[i].trucksNeeded} trucks` : ""} ·{" "}
                      {preview.rows[i].quantity} {r.truckingUnit}
                      {r.truckingUnit === "Hours" ? " per truck" : ""}
                      <Box>
                        Estimated trucking hours:{" "}
                        {new Decimal(preview.plans[r.rowId!] ?? "0")
                          .mul(c.roundTripHours || "0")
                          .toDecimalPlaces(4)
                          .toString()}
                      </Box>
                      <Box>
                        {(() => {
                          const result = rowQuantity(draft, preview.rows[i]);
                          return `${hourly ? `${preview.rows[i].trucksNeeded} trucks × ` : ""}${
                            preview.rows[i].quantity
                          } ${truck.truckingUnit} × ${money(Number(truck.rate) * 100)} = ${money(
                            result.quantity
                              .mul(truck.rate || 0)
                              .mul(100)
                              .toDecimalPlaces(0)
                              .toNumber()
                          )}`;
                        })()}
                      </Box>
                    </Alert>
                  )}
                </Box>
              );
            })}
          </>
        )}
        {(error ||
          (previewError && previewError !== "Enable production duration for linked rows.")) && (
          <Alert severity="error">{error || previewError}</Alert>
        )}
        {preview && !previewError && !selectedRow && (
          <Alert severity="info">
            Working duration: {preview.hours?.toDecimalPlaces(4).toString() ?? "not configured"}{" "}
            hours · Travel: {preview.travel.toDecimalPlaces(4).toString()} hours · Total costs:{" "}
            {total}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 1 }}>
          <UiButton uiVariant="outlined-small" onClick={onClose}>
            Cancel
          </UiButton>
          {!readOnly && (
            <UiButton
              uiVariant="primary-green"
              disabled={!!previewError || (tab === 1 && !p.enabled)}
              onClick={() => {
                onApply(draft);
                onClose();
              }}
            >
              Apply
            </UiButton>
          )}
        </Box>
      </Box>
    </UiSideModal>
  );
}
